/**
 * What the first session of a mapped app will pay, from its EFFECTIVE flags
 * (STORY-022).
 *
 * Mirrors the orchestrator's eager-download rule (orc-clerical
 * `handle_provision`): a worker fetches an app at desired-state time only
 * when `precache` is on OR the app has a warm target (a warm slot cannot be
 * built from bits that are not there). Otherwise the app is lazy — it joins
 * the board at once and the binary is pulled on first job, so the first user
 * waits for download, extract and bake, and reads that as "the mapping took
 * a long time to take effect".
 *
 * Pure: takes resolved values, not overrides — the caller applies the pool
 * default for `null` exactly as the toggles do.
 */
export interface FirstSessionCost {
  /** True when workers fetch the app when the mapping lands, before any session. */
  fetchedAtSave: boolean;
  /** One line for the card. */
  text: string;
  /** What made it so — for the tooltip. */
  because: string;
}

export function firstSessionCost(precache: boolean, warmTarget: number): FirstSessionCost {
  if (precache && warmTarget > 0) {
    return {
      fetchedAtSave: true,
      text: 'Fetched when saved — workers download this app before any session.',
      because: 'precache is on and a warm target is set',
    };
  }
  if (precache) {
    return {
      fetchedAtSave: true,
      text: 'Fetched when saved — workers download this app before any session.',
      because: 'precache is on',
    };
  }
  if (warmTarget > 0) {
    return {
      fetchedAtSave: true,
      text: 'Fetched when saved — a warm slot needs the app on disk.',
      because: `warm target is ${warmTarget}`,
    };
  }
  return {
    fetchedAtSave: false,
    text: 'Fetched on first session — the first user waits for download, extract and bake.',
    because: 'precache is off and warm target is 0; turn precache on to fetch at save time',
  };
}
